import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { type ReactNode, StrictMode } from 'react'
import { hydrateRoot, createRoot } from 'react-dom/client'
import Layout from '../layouts/Layout'

// NOTE: '@gravity-ui/uikit/styles/fonts.css' is deliberately NOT imported.
// Its only content is `@import url("https://fonts.googleapis.com/...Inter...")`,
// which sent every visitor's IP to Google for a font the site never shows —
// global.css overrides --g-font-family-sans to Montserrat for Gravity's
// components too, so Inter was downloaded and then rendered by nothing.
// maplibre's stylesheet is imported by LocationMapInner, so it ships only
// with the map chunk.
import '@gravity-ui/uikit/styles/styles.css'
import './global.css'

// One chunk per page. With `eager: true` every page (admin, cabinet,
// calendars, share kits…) landed in a single 2.3 MB bundle and the home page
// paid for all of it (Lighthouse: 470-570 KB unused JS on every URL, audit
// 2026-10-09). Vite turns each entry here into a dynamic import.
const pages = import.meta.glob<{ default: ResolvedComponent }>('../pages/**/*.tsx')

// A deploy replaces the hashed chunk files while open tabs still hold the old
// manifest; the next client-side visit then fails to fetch its page chunk.
// Remember where that visit was going and finish it with a full page load
// instead of showing Inertia's error dialog.
let pendingVisitUrl: string | null = null
router.on('before', (event) => {
  pendingVisitUrl = event.detail.visit.url.href
})

void createInertiaApp({
  title: (title) => (title ? `${title} — LiluPets` : 'LiluPets'),

  resolve: async (name) => {
    const load = pages[`../pages/${name}.tsx`]
    if (!load) {
      throw new Error(`Page not found: ${name}`)
    }

    let page: { default: ResolvedComponent }
    try {
      page = await load()
    } catch (error) {
      if (pendingVisitUrl && pendingVisitUrl !== window.location.href) {
        window.location.assign(pendingVisitUrl)
        return new Promise<never>(() => {})
      }
      throw error
    }

    page.default.layout ||= (page: ReactNode) => <Layout>{page}</Layout>

    return page
  },

  setup({ el, App, props }) {
    if (el.dataset.serverRendered === 'true') {
      hydrateRoot(
        el,
        <StrictMode>
          <App {...props} />
        </StrictMode>,
      )
    } else {
      createRoot(el).render(
        <StrictMode>
          <App {...props} />
        </StrictMode>,
      )
    }
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: false,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  if (document.getElementById('app')) {
    throw error
  }
})
